/* eslint-disable @next/next/link-passhref */
import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import H4 from '@base/H4/H4';
import { Spacings } from '@styles/index';
import Link from 'next/link';
import Image from '@base/Image';
import { getFullName } from '@models/User';
import { NOTES } from '@constants/segment';
import isEmptyObject from '@utils/isEmptyObject';
import { getImage } from '@utils/imageHelper';
import P2 from '@base/P2/P2';
import H3 from '@base/H3/H3';
import getTopicGroupAssetObj from '../constants/getTopicGroupAssetObj';
import TopicCard from './TopicCard';
import TopicFilters from './TopicFilters';

interface ITopicsProps {
  goalUID: string;
  topicUID?: string;
  goalSlug: string | string[];
  data: any;
  educatorUID?: string;
  educatorUsername?: string;
  onEducatorSelection?: (educatorUid) => void;
  topicFilters?: {
    educatorOptions: [];
    subjectOptions: [];
  };
  selectedEducator?: string;
  isRecordedGoal?: boolean;
  source?: string;
  topicCardClick?: (topic) => void;
  isGTP?: boolean;
  isLoading?: boolean;
}

const TopicGroupOuterWrapper = styled.div`
  grid-column: span 20;
`;

const TopicGroupWrapper = styled.div`
  margin-bottom: ${Spacings.SPACING_20B};
  &:last-child {
    margin-bottom: 0;
  }
`;

const TopicGroupName = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${Spacings.SPACING_6B};
`;

const StyH4 = styled(H4)`
  margin-left: ${Spacings.SPACING_4B};
`;

const TopicCardsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacings.SPACING_6B} ${Spacings.SPACING_4B};
`;

const Empty = styled.div`
  grid-column: span 18;
  text-align: center;
  margin-top: ${Spacings.SPACING_24B};
`;

const NoCourses = styled(H3)`
  margin-top: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_2};
`;

const EmptyImage = styled.img`
  user-drag: none;
  user-select: none;
`;

const getAuthorName = (topic) => {
  const { displayAuthor, authors } = topic;
  const { firstName, lastName } = displayAuthor;
  const fullName = getFullName(firstName, lastName);
  if (authors.length > 1) {
    return `${fullName} & ${authors.length - 1} more`;
  }
  return fullName;
};

const renderEmptyState = () => {
  return (
    <Empty>
      <EmptyImage src={`${getImage('gtp/no-results.svg')}`} alt="" />
      <NoCourses>No lessons found</NoCourses>
      <P2 color="var(--color-text-secondary)">
        There are no videos here. Try changing the filters
      </P2>
    </Empty>
  );
};

const Topics: React.FC<ITopicsProps> = ({
  goalUID,
  goalSlug,
  topicUID,
  data = [],
  educatorUID,
  educatorUsername,
  topicCardClick,
  onEducatorSelection,
  topicFilters,
  selectedEducator,
  isRecordedGoal,
  source,
  isGTP,
  isLoading
}) => {
  const topicCardsRef = React.useRef<HTMLDivElement>(null);
  const [selectedSubject, setSelectedSubject] = useState('all');

  useEffect(() => {
    if (topicUID) {
      const moveToTopicGroupTimer = setTimeout(() => {
        window.scrollTo({
          top: topicCardsRef.current?.offsetTop - 72,
          behavior: 'smooth'
        });
      }, 200);
      return () => clearTimeout(moveToTopicGroupTimer);
    }
  }, [topicUID]);

  const onTopicCardClick = (topic) => () => {
    if (topicCardClick) topicCardClick(topic);
  };

  const getHref = (uid, topic) => {
    if (source === NOTES) {
      return `/goal/${goalSlug}/${goalUID}/notes/${uid}/${topic.uid}`;
    }
    if (educatorUID && educatorUsername)
      return `/goal/${goalSlug}/${goalUID}/topic/${uid}/${topic.uid}?educatorUID=${educatorUID}&educatorUsername=${educatorUsername}`;

    return `/goal/${goalSlug}/${goalUID}/topic/${uid}/${topic.uid}`;
  };

  const renderTopicCard = (topic, color) => {
    if (isEmptyObject(topic)) return null;
    const {
      title,
      name,
      uid,
      totalSubItem,
      childrenCount,
      educatorCoveredTopic,
      isEpubAvailable,
      isNotesAvailable
    } = topic;
    const educatorName = isRecordedGoal ? getAuthorName(topic) : '';
    return (
      <TopicCard
        topicUID={uid}
        title={title || name}
        chaptersCount={totalSubItem || childrenCount || 0}
        onClick={onTopicCardClick(topic)}
        color={color}
        countSubtext={isGTP && 'sub-system'}
        educatorCoveredTopic={educatorCoveredTopic}
        isEpubAvailable={isEpubAvailable}
        isNotesAvailable={isNotesAvailable}
        source={source}
        educatorName={educatorName}
      />
    );
  };

  const displayData =
    selectedSubject === 'all' || !isRecordedGoal
      ? data
      : data.filter((topicGroup) => {
          return topicGroup.uid === selectedSubject;
        });

  const hasData = displayData.some((topicGroups) =>
    topicGroups?.children.some((topic) => !isEmptyObject(topic))
  );

  return (
    <TopicGroupOuterWrapper>
      {isRecordedGoal && (
        <TopicFilters
          subjectOptions={topicFilters?.subjectOptions}
          educatorOptions={topicFilters?.educatorOptions}
          onSubjectSelection={setSelectedSubject}
          onEducatorSelection={onEducatorSelection}
          selectedSubject={selectedSubject}
          selectedEducator={selectedEducator}
          goalUID={goalUID}
        />
      )}
      {displayData.map((topicGroups) => {
        const {
          title = '',
          children = [],
          uid,
          educatorCoveredTopicGroup = true,
          name = ''
        } = topicGroups;
        const { icon } = getTopicGroupAssetObj(title || name);
        const { color } = getTopicGroupAssetObj(title || name);

        const hasTopics = topicGroups?.children.some(
          (topic) => !isEmptyObject(topic)
        );

        if (!educatorCoveredTopicGroup || !hasTopics) return null;

        return (
          <TopicGroupWrapper
            key={uid}
            ref={uid === topicUID ? topicCardsRef : null}
          >
            <TopicGroupName>
              <Image
                height={Spacings.SPACING_10B}
                width={Spacings.SPACING_10B}
                alt="syllabus"
                src={icon}
              />
              <StyH4 data-testid="topicgroup-name">
                {topicGroups.title || topicGroups.name}
              </StyH4>
            </TopicGroupName>
            <TopicCardsWrapper>
              {children.map((topic) => {
                const {
                  educatorCoveredTopic = true,
                  isEpubAvailable = true,
                  isNotesAvailable
                } = topic;

                // Used in Notes Page
                if (source === NOTES && (isEpubAvailable || isNotesAvailable))
                  return (
                    <Link
                      href={getHref(uid, topic)}
                      key={`${topic.uid}-${uid}`}
                    >
                      <a target="_blank" id={`${topic.uid}-${uid}`}>
                        {renderTopicCard(topic, color)}
                      </a>
                    </Link>
                  );

                // Used in Syllabus and Educator Profile Page
                if (
                  educatorCoveredTopic &&
                  isEpubAvailable &&
                  !isEmptyObject(topic)
                )
                  return (
                    <Link
                      href={getHref(uid, topic)}
                      key={`${topic.uid}-${uid}`}
                      id={`${topic.uid}-${uid}`}
                    >
                      {renderTopicCard(topic, color)}
                    </Link>
                  );

                // Used in Educator Profile Page
                return <>{renderTopicCard(topic, color)}</>;
              })}
            </TopicCardsWrapper>
          </TopicGroupWrapper>
        );
      })}

      {!hasData && !isLoading && isRecordedGoal && renderEmptyState()}
    </TopicGroupOuterWrapper>
  );
};

Topics.defaultProps = {
  topicUID: '',
  educatorUID: '',
  educatorUsername: '',
  onEducatorSelection: () => {},
  topicFilters: {
    educatorOptions: [],
    subjectOptions: []
  },
  selectedEducator: '',
  isRecordedGoal: false,
  source: '',
  topicCardClick: () => {},
  isLoading: false
};

export default Topics;
